import { useQuery } from '@tanstack/react-query';
import { configApi, type ColumnConfig, type DocumentConfig, type ModuleVisibility } from '../api/config';
import { MODULE_PATHS } from '../config/labels';

/**
 * Per-city module master switches. Fail-open: while loading or on error the
 * returned map is empty, and callers treat a missing key as enabled.
 */
export function useModuleVisibility(enabled = true): ModuleVisibility {
  const { data } = useQuery({
    queryKey: ['config', 'module-visibility'],
    queryFn: configApi.getModuleVisibility,
    enabled,
    staleTime: 5 * 60_000,
  });
  return data ?? {};
}

/**
 * Whether a single module (entity/feature) is enabled for this city (fail-open).
 * This is the master switch consumed everywhere the module's data or links appear.
 */
export function useIsModuleEnabled(moduleKey: string, enabled = true): boolean {
  const mv = useModuleVisibility(enabled);
  return mv[moduleKey] !== false;
}

/** First enabled module's path, used as a redirect target for disabled ones. */
export function firstModulePath(mv: ModuleVisibility): string {
  for (const key of Object.keys(MODULE_PATHS)) {
    if (mv[key] !== false) return MODULE_PATHS[key];
  }
  return '/dashboard';
}

/**
 * Per-city column visibility for one overview list. Fail-open: a missing key is
 * treated as visible, so guards should check `cols[key] !== false`.
 */
export function useColumnConfig(listKey: string): Record<string, boolean> {
  const { data } = useQuery<ColumnConfig>({
    queryKey: ['config', 'column-config'],
    queryFn: configApi.getColumnConfig,
    staleTime: 5 * 60_000,
  });
  return data?.[listKey] ?? {};
}

/**
 * Documents feature config for one entity. Fail-open: while loading, all three
 * actions (tab/upload/create) are treated as enabled.
 */
export function useDocumentConfig(entity: string): { tab: boolean; upload: boolean; create: boolean } {
  const { data } = useQuery<DocumentConfig>({
    queryKey: ['config', 'document-config'],
    queryFn: configApi.getDocumentConfig,
    staleTime: 5 * 60_000,
  });
  return data?.[entity] ?? { tab: true, upload: true, create: true };
}
